import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { LIVE_ACCOUNT_URL } from "../lib/api";
import { useBlog } from "../hooks/useBlogs";

function BlogNotFound() {
    return (
        <div
            data-testid="blog-detail-not-found"
            className="pt-36 pb-20 xpo-container"
        >
            <p className="text-slate-400">Post not found.</p>
            <Link to="/blogs" className="btn-outline-mint mt-4 inline-flex">
                <ArrowLeft size={16} /> Back to blogs
            </Link>
        </div>
    );
}

function BlogHeader({ post }) {
    return (
        <>
            <Link
                to="/blogs"
                className="text-sm text-slate-400 hover:text-[#A6E3DC] inline-flex items-center gap-2 mb-8"
            >
                <ArrowLeft size={16} /> All articles
            </Link>
            <p className="overline mb-3">{post.category}</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.1]">
                {post.title}
            </h1>
            <p className="text-slate-500 text-sm mt-4">
                {new Date(post.published_at).toLocaleDateString(undefined, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                })}{" "}
                · {post.author} · {post.read_minutes} min read
            </p>
            <img
                src={post.cover_image}
                alt={post.title}
                className="w-full rounded-2xl border border-white/10 mt-10 aspect-[16/9] object-cover"
            />
        </>
    );
}

function BlogBody({ post }) {
    return (
        <div className="prose-invert mt-10 max-w-none">
            {post.content.split("\n\n").map((para, i) => (
                <p
                    key={`${post.slug}-para-${i}`}
                    className="text-slate-300 text-lg leading-relaxed mb-5 whitespace-pre-line"
                >
                    {para}
                </p>
            ))}
        </div>
    );
}

function BlogCTA() {
    return (
        <div className="mt-14 p-8 lg:p-10 xpo-glass">
            <p className="overline mb-2">Ready to trade?</p>
            <h3 className="text-2xl font-bold text-white">
                Put the theory to work on a live or demo account.
            </h3>
            <a
                href={LIVE_ACCOUNT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="blog-detail-cta-open-account"
                className="btn-primary mt-6"
            >
                Open an account <ArrowUpRight size={16} />
            </a>
        </div>
    );
}

export default function BlogDetailPage() {
    const { slug } = useParams();
    const { post, error } = useBlog(slug);

    if (error) return <BlogNotFound />;
    if (!post)
        return (
            <div className="pt-36 pb-20 xpo-container">
                <p className="text-slate-500">Loading…</p>
            </div>
        );

    return (
        <article data-testid="blog-detail-page" className="pt-32 pb-20">
            <div className="xpo-container max-w-4xl">
                <BlogHeader post={post} />
                <BlogBody post={post} />
                <BlogCTA />
            </div>
        </article>
    );
}
